import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import DataTableElement from "../../../components/elements/DataTableElement";

import { Columns } from "./Columns";
import { iaElementsResource, servicesOrderAISave, getSupplierTypes } from "../../../helpers/configRoutes/backend_helper";
import AddUpServiceOrder from "../../serviceOrders/AddUpServiceOrder";
import { useDispatch } from "react-redux";
import { reset_service, update_service } from "../../../store/serviceOrders/action";

const ServiceOrdersPre = () => {
    const { id } = useParams()
    const navigate = useNavigate()
    const dispatch = useDispatch()
    const [selectedRows, setSelectedRows] = useState([]);
    const [load, setLoad] = useState({ show: false, textLoad: 'Loading accounts...' });
    const [item, setItem] = useState({});
    const [data, setData] = useState([]);
    const [supplierTypesMap, setSupplierTypesMap] = useState({});
    const [modal, setModal] = useState({ show: false, title: 'New Service', rowId: null })

    const formatDate = (value) => {
        if (!value) {
            return '';
        }

        const date = new Date(value);

        if (Number.isNaN(date.getTime())) {
            return '';
        }

        const yyyy = date.getFullYear();
        const mm = String(date.getMonth() + 1).padStart(2, '0');
        const dd = String(date.getDate()).padStart(2, '0');

        return `${yyyy}-${mm}-${dd}`;
    };

    const normalizeAiRows = (rows = [], typesMap = {}) => rows.map((row, index) => ({
        ai_row_id: row.ai_row_id || `${index}-${row.title || row.description || 'service'}`,
        title: row.title || row.description || `Suggested service ${index + 1}`,
        description: row.description || row.title || '',
        priority: row.priority || 'medium',
        estimated_km: row.estimated_km ?? '',
        supplier_type_id: row.supplier_type_id || '',
        supplier_type_name: typesMap[row.supplier_type_id] || row.supplier_type_name || '',
        ready_to_create: Boolean(row.ready_to_create),
        ...row,
    }));

    const isCreatedBatch = item.status?.id === 41;
    const readyCount = data.filter((row) => row.ready_to_create).length;
    const pendingCount = isCreatedBatch ? 0 : Math.max(data.length - readyCount, 0);

    const buildServiceDraft = (serviceOrderItem = {}) => {
        const today = formatDate(new Date());

        return {
            id: serviceOrderItem.id || '',
            internalId: serviceOrderItem.internalId || '',
            description: serviceOrderItem.description || serviceOrderItem.title || '',
            creation_date: serviceOrderItem.creation_date || today,
            due_date: serviceOrderItem.due_date || today,
            estimated_cost: serviceOrderItem.estimated_cost || '',
            real_cost: serviceOrderItem.real_cost || '',
            status_id: serviceOrderItem.status_id || '',
            status: serviceOrderItem.status || {
                id: '',
                name: '',
                color: '',
            },
            supplier_id: serviceOrderItem.supplier_id || '',
            supplier_type_id: serviceOrderItem.supplier_type_id || '',
            supplier: serviceOrderItem.supplier || {
                id: '',
                name: '',
            },
            trailer_id: serviceOrderItem.trailer_id || '',
            trailer: serviceOrderItem.trailer || {
                id: '',
                number: '',
                name: '',
            },
            truck_id: serviceOrderItem.truck_id || item.makeable_id || '',
            truck: serviceOrderItem.truck || {
                id: item.makeable_id || '',
                number: item.makeable?.number || '',
                name: item.makeable?.number || '',
            },
            service_request_id: serviceOrderItem.service_request_id || '',
            service_request: serviceOrderItem.service_request || null,
            mileage_control: serviceOrderItem.mileage_control || 0,
            current_mileage_truck: serviceOrderItem.current_mileage_truck || 0,
            miles_interval_truck: serviceOrderItem.miles_interval_truck || 0,
            current_mileage_trailer: serviceOrderItem.current_mileage_trailer || 0,
            miles_interval_trailer: serviceOrderItem.miles_interval_trailer || 0,
            ai_row_id: serviceOrderItem.ai_row_id,
            title: serviceOrderItem.title || '',
            priority: serviceOrderItem.priority || 'medium',
            estimated_km: serviceOrderItem.estimated_km ?? '',
            ready_to_create: serviceOrderItem.ready_to_create || false,
        };
    };

    useEffect(() => {
        const getData = async () => {
            setLoad((prev) => ({ ...prev, show: true }))

            try {
                const [response, supplierTypesResponse] = await Promise.all([
                    iaElementsResource('show', {}, id),
                    getSupplierTypes(),
                ]);

                const typesMap = (supplierTypesResponse?.types || []).reduce((acc, type) => {
                    acc[type.id] = type.name;
                    return acc;
                }, {});

                setSupplierTypesMap(typesMap);
                setItem(response.element);
                setData(normalizeAiRows(response.element?.ia_response || [], typesMap));
            } finally {
                setLoad((prev) => ({ ...prev, show: false }))
            }
        };
        getData();
    }, [id]);

    const openModalAdd = (serviceOrderItem = {}) => {
        dispatch(reset_service())
        dispatch(update_service(buildServiceDraft(serviceOrderItem)))
        setModal({
            show: true,
            rowId: serviceOrderItem.ai_row_id,
            title: "Complete Service Order",
        });
    };

    const handleModalDraftSave = async (values) => {
        const updatedRow = {
            ...values,
            ai_row_id: values.ai_row_id,
            title: values.title || values.description,
            priority: values.priority || 'medium',
            estimated_km: values.estimated_km ?? '',
            supplier_type_id: values.supplier_type_id || '',
            supplier_type_name: supplierTypesMap[values.supplier_type_id] || values.supplier_type_name || '',
            ready_to_create: true,
        };

        setData((prev) => prev.map((row) => row.ai_row_id === values.ai_row_id ? updatedRow : row));
        setSelectedRows((prev) => {
            const withoutCurrent = prev.filter((row) => row.ai_row_id !== values.ai_row_id);
            return [...withoutCurrent, updatedRow];
        });

        return { status: true };
    };

    const handleGuardar = async() => {
        const response = await servicesOrderAISave({selectedRows:selectedRows,element:item})
        if(response.status){
            navigate('/ai-operations')
        }
    }

    const cols = Columns(selectedRows, setSelectedRows, openModalAdd, item)
    return (
        <div className="p-8 space-y-6">
            <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
                <div>
                    <div className="text-2xl font-semibold text-cs-dark">Service Orders</div>
                    <p className="mt-1 text-sm text-cs-muted">
                        Review the AI suggestions, complete each modal, and only then create the final maintenance orders.
                    </p>
                </div>

                {!isCreatedBatch && selectedRows.length > 0 && (
                    <button
                        onClick={handleGuardar}
                        className="inline-flex items-center justify-center rounded-lg bg-blue-600 px-4 py-2 text-sm font-medium text-white shadow-sm transition hover:bg-blue-700"
                    >
                        Create selected service orders ({selectedRows.length})
                    </button>
                )}
            </div>

            <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-4">
                <div className="rounded-xl border border-cs-border bg-white p-4 shadow-sm">
                    <p className="text-xs font-semibold uppercase tracking-[0.16em] text-cs-muted">Suggestions</p>
                    <p className="mt-2 text-3xl font-semibold text-cs-dark">{data.length}</p>
                </div>
                <div className="rounded-xl border border-amber-100 bg-amber-50 p-4 shadow-sm">
                    <p className="text-xs font-semibold uppercase tracking-[0.16em] text-amber-700">Pending completion</p>
                    <p className="mt-2 text-3xl font-semibold text-amber-900">{pendingCount}</p>
                </div>
                <div className="rounded-xl border border-blue-100 bg-blue-50 p-4 shadow-sm">
                    <p className="text-xs font-semibold uppercase tracking-[0.16em] text-blue-700">Ready to create</p>
                    <p className="mt-2 text-3xl font-semibold text-blue-900">{readyCount}</p>
                </div>
                <div className="rounded-xl border border-emerald-100 bg-emerald-50 p-4 shadow-sm">
                    <p className="text-xs font-semibold uppercase tracking-[0.16em] text-emerald-700">Batch status</p>
                    <p className="mt-2 text-lg font-semibold text-emerald-900">
                        {isCreatedBatch ? 'Created' : 'In review'}
                    </p>
                </div>
            </div>

            <div className="overflow-hidden rounded-lg bg-white shadow-md">
                <DataTableElement
                    columns={cols}
                    items={data}
                    tableLoad={load}
                />
            </div>

            {modal.show && (
                <AddUpServiceOrder
                    modal={modal}
                    setModal={setModal}
                    onSubmitOverride={handleModalDraftSave}
                />
            )}
        </div>
    );
}

export default ServiceOrdersPre;
